import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores, useFocus, useSearchInput } from 'hooks';
import PropTypes from 'prop-types';
import { Icon } from 'components/project/ClerkScreen';
import { clerkColors } from 'shared/utils/theme';
import { FilterButtons } from 'components/project/ClerkScreen/SideNavContainer/BidActivity';
import NoResultsSVG from './NoResultsSvg';

import {
  ItemSearch,
  SearchInputCont,
  SearchIcon,
  SearchSpinner,
  InstructionContainer,
  Instruction,
  Item,
  ItemData,
  ItemListContainer,
  ItemList,
  ItemTitle,
  SectionTitle,
  NoResults,
  NoResultsTitle,
  NoResultsTip,
  SearchInput,
} from './Styles';

const RunList = ({ onRunListItemClick }) => {
  const {
    AuctionStore: { runList },
    BidStore: { auction },
    WebSocketStore: { sendFocusSidePanelSearchMessage },
  } = useStores();

  const [searchInputRef, setSearchInputRefFocus] = useFocus();

  const searchChangeDataMatcher = (data, searchTerm) => data.filter(
    (item) => item.product.toUpperCase().indexOf(searchTerm.toUpperCase()) > -1
        || item.lotNumber.toUpperCase().indexOf(searchTerm.toUpperCase()) > -1,
  );

  const filterClickDataMatcher = (data, filterValue) => data.filter((item) => item.productState === filterValue);

  const {
    setIsLoading,
    isLoading,
    isSearchTermEmpty,
    filteredData,
    events: { handleFilterClick, handleSearchChange },
  } = useSearchInput(searchChangeDataMatcher, {
    data: runList,
    filterClickDataMatcher,
  });

  useEffect(() => {
    if (auction.focusOnSidePanelSearch) {
      setSearchInputRefFocus();
    }

    setIsLoading(false);
    // eslint-disable-next-line
  }, [auction.focusOnSidePanelSearch]);

  const filterButtonMappings = [
    {
      text: null,
      icon: 'times',
      bgColor: '#e7e7e7',
      color: '#000000',
      bgVariance: -20,
      value: null,
    },
    {
      text: 'Not Statused',
      bgColor: '#e7e7e7',
      color: '#000000',
      bgVariance: -20,
      value: 0,
    },
    {
      text: 'Pulled',
      bgColor: '#e7e7e7',
      color: '#000000',
      bgVariance: -20,
      value: 5,
    },
    {
      text: 'Subject To',
      bgColor: '#FBCC08',
      color: '#000000',
      bgVariance: 20,
      value: 2,
    },
    {
      text: 'Sold',
      bgColor: '#21BA45',
      color: '#FFFFFF',
      bgVariance: 20,
      value: 3,
    },
    {
      text: 'No Sale',
      bgColor: '#DB2828',
      color: '#FFFFFF',
      bgVariance: 50,
      value: 4,
    },
  ];

  // TODO: Open Item should be updated when we starting bidding on an item.
  return (
    <ItemSearch>
      <SearchInputCont>
        <SearchInput
          autoFocus
          placeholder="Search items by name, lot number"
          ref={searchInputRef}
          onChange={handleSearchChange}
          onFocus={() => sendFocusSidePanelSearchMessage()}
        />
        <SearchIcon type="search" size={22} />
        {isLoading && <SearchSpinner />}
      </SearchInputCont>
      <InstructionContainer>
        <i className="fa fa-question-circle" aria-hidden="true" />
        <Instruction>
          You may click one of the items from the list below to load into the
          auction window.
        </Instruction>
      </InstructionContainer>

      {filterButtonMappings.length && (
        <FilterButtons
          mappings={filterButtonMappings}
          onClick={handleFilterClick}
        />
      )}

      {!isLoading && filteredData.length > 0 && (
        <>
          <SectionTitle>
            Run List (
            {filteredData.length}
            )
          </SectionTitle>
          <ItemListContainer>
            <ItemList data-testid="runlist-items">
              <RenderItem
                data={filteredData}
                onClick={onRunListItemClick}
                isOpenForBidding={auction.isOpenForBidding}
              />
            </ItemList>
          </ItemListContainer>
        </>
      )}

      {!isSearchTermEmpty && !isLoading && filteredData.length === 0 && (
        <NoResults>
          <NoResultsSVG />
          <NoResultsTitle>
            We couldn&apos;t find anything matching your search
          </NoResultsTitle>
          <NoResultsTip>Try again with a different item.</NoResultsTip>
        </NoResults>
      )}
    </ItemSearch>
  );
};

RunList.propTypes = {
  onRunListItemClick: PropTypes.func.isRequired,
};

const RenderItem = ({ data, onClick, isOpenForBidding }) => {
  // TODO: Move to ProviderStore
  const productStateMappings = [
    {
      productState: 0,
      productStateName: 'none',
      bgColor: 'default',
      color: 'black',
      hoverVariance: -20,
    },
    {
      productState: 1,
      productStateName: 'open',
      bgColor: 'default',
      color: 'black',
      hoverVariance: -20,
    },
    {
      productState: 2,
      productStateName: 'subjectTo',
      bgColor: 'yellow',
      color: 'black',
      hoverVariance: 20,
    },
    {
      productState: 3,
      productStateName: 'sold',
      bgColor: 'green',
      color: 'white',
      hoverVariance: 20,
    },
    {
      productState: 4,
      productStateName: 'noSale',
      bgColor: 'red',
      color: 'white',
      hoverVariance: 50,
    },
    {
      productState: 5,
      productStateName: 'pulled',
      bgColor: '#666666',
      color: 'white',
      hoverVariance: -20,
    },
  ];

  const getColorsForRunListItem = (state) => {
    const mapping = productStateMappings.filter(
      (item) => item.productState === state,
    )[0];

    return {
      bgColor: clerkColors[mapping.bgColor],
      color: clerkColors[mapping.color],
      hoverVariance: mapping.hoverVariance,
    };
  };

  return data.map((item) => {
    const { color, bgColor, hoverVariance } = getColorsForRunListItem(
      item.productState,
    );

    return (
      <Item
        testid={`runlist-item-${item.lotNumber}`}
        color={color}
        bgColor={bgColor}
        hoverVariance={hoverVariance}
        key={item.lotNumber}
        closed={item.closed}
        disabled={isOpenForBidding}
        onClick={() => onClick(item.lotNumber)}
      >
        {item.productState === 0 ? (
          <Icon icon="check-circle-o" />
        ) : (
          <Icon icon="gavel" />
        )}
        <img loading="lazy" src={item.url} alt={item.product} />
        <ItemData>
          <ItemTitle color={color}>
            Item#
            {' '}
            {item.lotNumber}
            :
            <br />
            {item.product}
          </ItemTitle>
        </ItemData>
      </Item>
    );
  });
};

export default observer(RunList);
